import React, { useState, useEffect } from 'react';
import { useNavigation } from '../../context/NavigationContext';
import { Cookie, Shield, Check, X, Settings2, ExternalLink } from 'lucide-react';

interface CookiePreferences {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  consentedAt: number;
}

const STORAGE_KEY = 'adio_cookie_consent_v1';

export const CookieConsentBanner: React.FC = () => {
  const { navigate } = useNavigation();
  const [isOpen, setIsOpen] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    functional: true,
    analytics: false,
    marketing: false,
    consentedAt: 0,
  });

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        // Show after brief initial delay for smooth UX
        const timer = setTimeout(() => setIsOpen(true), 1200);
        return () => clearTimeout(timer);
      } else {
        const parsed = JSON.parse(stored);
        setPreferences(parsed);
      }
    } catch {
      // safe fallback
    }
  }, []);

  const saveConsent = (prefs: CookiePreferences) => {
    try {
      const toSave = { ...prefs, consentedAt: Date.now() };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
      setPreferences(toSave);
      setIsOpen(false);
      setShowPreferences(false);
    } catch (err) {
      console.warn('Cookie consent save warning:', err);
    }
  };

  const handleAcceptAll = () => {
    saveConsent({
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
      consentedAt: Date.now(),
    });
  };

  const handleAcceptEssentialOnly = () => {
    saveConsent({
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
      consentedAt: Date.now(),
    });
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-0 inset-x-0 z-50 p-4 sm:p-6 animate-in slide-in-from-bottom-5 duration-300 pointer-events-none">
      <div className="max-w-4xl mx-auto bg-slate-900/95 backdrop-blur-xl border border-slate-700/80 rounded-2xl shadow-2xl p-5 sm:p-6 text-slate-100 pointer-events-auto">
        {!showPreferences ? (
          /* Simple Standard Banner */
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-5">
            <div className="flex items-start gap-3.5">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400 shrink-0 mt-0.5">
                <Cookie className="w-5 h-5" />
              </div>
              <div className="space-y-1">
                <h4 className="text-sm font-bold text-white flex items-center gap-2">
                  <span>Çerez ve Gizlilik Tercihleriniz</span>
                </h4>
                <p className="text-xs text-slate-300 leading-relaxed max-w-2xl">
                  Platformumuzda temel oturum ve güvenlik işlevlerini yerine getirmek amacıyla zorunlu çerezler kullanılmaktadır. 
                  Deneyiminizi geliştirmek ve hizmetlerimizi optimize etmek için izninize bağlı çerezleri yönetebilirsiniz.{' '}
                  <button
                    onClick={() => navigate('/cerez-politikasi')}
                    className="text-blue-400 hover:text-blue-300 underline font-medium inline-flex items-center gap-0.5"
                  >
                    <span>Çerez Politikası</span>
                    <ExternalLink className="w-3 h-3" />
                  </button>
                </p>
              </div>
            </div>

            <div className="flex flex-wrap sm:flex-nowrap items-center gap-2.5 shrink-0">
              <button
                onClick={handleAcceptAll}
                className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs shadow-md transition whitespace-nowrap active:scale-95"
              >
                Tümünü Kabul Et
              </button>
              <button
                onClick={handleAcceptEssentialOnly}
                className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-semibold text-xs border border-slate-700 transition whitespace-nowrap"
              >
                Sadece Zorunlu
              </button>
              <button
                onClick={() => setShowPreferences(true)}
                className="w-full sm:w-auto px-3 py-2.5 rounded-xl text-slate-400 hover:text-white font-semibold text-xs transition flex items-center justify-center gap-1.5"
              >
                <Settings2 className="w-3.5 h-3.5" />
                <span>Tercihleri Yönet</span>
              </button>
            </div>
          </div>
        ) : (
          /* Preference Management Drawer / Modal */
          <div className="space-y-5">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center gap-2 text-sm font-bold text-white">
                <Settings2 className="w-4 h-4 text-blue-400" />
                <span>Çerez Tercihlerini Yönet</span>
              </div>
              <button
                onClick={() => setShowPreferences(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              {/* Necessary */}
              <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 flex items-start justify-between gap-3">
                <div>
                  <span className="font-bold text-white block">Zorunlu Çerezler</span>
                  <span className="text-slate-400 text-[11px] leading-relaxed">
                    Oturum güvenliği, CSRF koruması ve temel platform fonksiyonları için zorunludur.
                  </span>
                </div>
                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 shrink-0">
                  Zorunlu
                </span>
              </div>

              {/* Functional */}
              <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 flex items-start justify-between gap-3">
                <div>
                  <span className="font-bold text-white block">İşlevsel Çerezler</span>
                  <span className="text-slate-400 text-[11px] leading-relaxed">
                    Tema tercihleri, dil ve editör araç çubuğu ayarlarınızın hatırlanmasını sağlar.
                  </span>
                </div>
                <input
                  type="checkbox"
                  checked={preferences.functional}
                  onChange={(e) => setPreferences({ ...preferences, functional: e.target.checked })}
                  className="rounded border-slate-700 bg-slate-800 text-blue-600 focus:ring-0 w-4 h-4 mt-1 cursor-pointer"
                />
              </div>

              {/* Analytics */}
              <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 flex items-start justify-between gap-3">
                <div>
                  <span className="font-bold text-white block">Performans ve Analitik</span>
                  <span className="text-slate-400 text-[11px] leading-relaxed">
                    Sayfa hızı ve platform performansını anonim ölçümlememize imkân tanır.
                  </span>
                </div>
                <input
                  type="checkbox"
                  checked={preferences.analytics}
                  onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                  className="rounded border-slate-700 bg-slate-800 text-blue-600 focus:ring-0 w-4 h-4 mt-1 cursor-pointer"
                />
              </div>

              {/* Marketing */}
              <div className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 flex items-start justify-between gap-3">
                <div>
                  <span className="font-bold text-white block">Pazarlama ve Hedefleme</span>
                  <span className="text-slate-400 text-[11px] leading-relaxed">
                    İlgi alanlarınıza uygun ürün ve yenilik duyuruları için kullanılır.
                  </span>
                </div>
                <input
                  type="checkbox"
                  checked={preferences.marketing}
                  onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                  className="rounded border-slate-700 bg-slate-800 text-blue-600 focus:ring-0 w-4 h-4 mt-1 cursor-pointer"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                onClick={() => setShowPreferences(false)}
                className="px-4 py-2 rounded-xl text-slate-400 hover:text-white text-xs font-semibold"
              >
                Geri Dön
              </button>
              <button
                onClick={handleSavePreferences}
                className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs shadow-md transition"
              >
                Tercihleri Kaydet
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default CookieConsentBanner;
